import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Car, Clock, Calendar, CreditCard, User, LogOut, ChevronRight, 
  Award, TrendingUp, MapPin, Star, ArrowUpRight
} from 'lucide-react';
import { getClientDashboard, clientLogout } from '../api';
import { MyRouteCard } from '../components/MyRouteCard';

const ClientPortal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [client, setClient] = useState(location.state?.client || null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const tierConfig = {
    lobby: { color: 'bg-gray-700', label: 'Lobby', accent: 'text-gray-300' },
    condo: { color: 'bg-red-600', label: 'Condo', accent: 'text-red-400' },
    penthouse: { color: 'bg-gradient-to-r from-yellow-500 to-yellow-700', label: 'Penthouse', accent: 'text-yellow-400' }
  };

  useEffect(() => {
    const loadDashboard = async () => {
      if (location.hash?.includes('session_id=')) return;
      if (location.state?.client) {
        setClient(location.state.client);
        window.history.replaceState({}, document.title);
      }
      try {
        const data = await getClientDashboard();
        setDashboard(data);
        setClient(data.client);
      } catch (err) {
        if (err.response?.status === 401) { navigate('/portal/login'); return; }
        setError('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    loadDashboard();
  }, [navigate, location]);

  const handleLogout = async () => {
    try { await clientLogout(); navigate('/portal/login'); } catch (err) { console.error(err); }
  };

  if (location.hash?.includes('session_id=')) {
    const ClientAuthCallback = React.lazy(() => import('./ClientAuthCallback'));
    return (
      <React.Suspense fallback={<div className="min-h-screen bg-gray-100 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600" /></div>}>
        <ClientAuthCallback />
      </React.Suspense>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your portal...</p>
        </div>
      </div>
    );
  }

  const memberTier = client?.membership_tier || 'lobby';
  const tier = tierConfig[memberTier] || tierConfig.lobby;

  return (
    <div className="min-h-screen bg-gray-100" data-testid="client-portal">
      {/* Header */}
      <header className="bg-black text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="h-10 w-10 bg-red-600 rounded-lg flex items-center justify-center">
                <Car className="h-6 w-6" />
              </div>
              <div className="hidden sm:block">
                <span className="font-bold text-lg">Client Portal</span>
                <span className="text-xs text-gray-400 block">YourAutoConcierge</span>
              </div>
            </Link>
            <div className="flex items-center gap-2 sm:gap-4">
              {client && (
                <div className="flex items-center gap-2">
                  {client.picture && <img src={client.picture} alt={client.name} className="h-8 w-8 rounded-full" />}
                  <span className="text-sm text-gray-300 hidden sm:block">{client.name || client.email}</span>
                </div>
              )}
              <Button onClick={handleLogout} variant="ghost" size="sm" className="text-gray-300 hover:text-white hover:bg-gray-800">
                <LogOut className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">{error}</div>}

        {/* Welcome + Membership */}
        <div className="grid md:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <Card className="md:col-span-2 p-5 sm:p-6 border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-black mb-1" data-testid="portal-welcome">
                  Welcome back, {client?.name?.split(' ')[0] || 'Member'}
                </h1>
                <p className="text-gray-600 text-sm">Your ACE membership overview</p>
              </div>
              <div className={`px-4 py-2 rounded-full text-white text-sm font-semibold ${tier.color} w-fit`} data-testid="portal-tier-badge">
                {tier.label} Member
              </div>
            </div>
            
            {dashboard?.membership_benefits && (
              <div className="mt-5 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Award className="h-5 w-5 text-red-600" />
                  <span className="font-semibold text-black text-sm">Your Benefits</span>
                  <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Save {dashboard.membership_benefits.discount}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {dashboard.membership_benefits.perks.map((perk, idx) => (
                    <span key={idx} className="px-3 py-1 bg-white border border-gray-200 rounded-full text-xs text-gray-700">{perk}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Upgrade CTA */}
            {memberTier !== 'penthouse' && (
              <div className="mt-4 flex items-center justify-between p-3 bg-red-50 border border-red-100 rounded-lg">
                <div className="flex items-center gap-2">
                  <ArrowUpRight className="h-4 w-4 text-red-600" />
                  <span className="text-sm text-gray-700">Upgrade to <strong>{memberTier === 'lobby' ? 'Condo' : 'Penthouse'}</strong> for more benefits</span>
                </div>
                <Button onClick={() => navigate('/membership')} size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-600 hover:text-white text-xs" data-testid="portal-upgrade-btn">
                  View Tiers
                </Button>
              </div>
            )}
          </Card>

          {/* Stats */}
          <Card className="p-5 sm:p-6 border-gray-200 bg-black text-white">
            <h3 className="text-base font-bold mb-4 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-red-600" /> Your Stats
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-300 text-sm flex items-center gap-2"><Car className="h-4 w-4 text-gray-500" /> Rides</span>
                <span className="text-2xl font-bold text-red-500">{dashboard?.stats?.total_rides || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300 text-sm flex items-center gap-2"><Clock className="h-4 w-4 text-gray-500" /> Hours</span>
                <span className="text-2xl font-bold text-red-500">{dashboard?.stats?.total_hours || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300 text-sm flex items-center gap-2"><Star className="h-4 w-4 text-gray-500" /> Since</span>
                <span className="text-sm font-semibold text-gray-400">
                  {client?.member_since ? new Date(client.member_since).toLocaleDateString('en-US', {month: 'short', year: 'numeric'}) : 'N/A'}
                </span>
              </div>
            </div>
          </Card>
        </div>

        {/* My Route — Auto Lifestyle GPS (P4) */}
        <MyRouteCard />

        {/* Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4 mb-6 sm:mb-8">
          {[
            { label: 'My Bookings', sub: 'View history', icon: Calendar, bg: 'bg-blue-100', color: 'text-blue-600', path: '/portal/bookings', testId: 'portal-bookings-card' },
            { label: 'Payments', sub: 'Receipts', icon: CreditCard, bg: 'bg-green-100', color: 'text-green-600', path: '/portal/payments', testId: 'portal-payments-card' },
            { label: 'Rewards', sub: 'Points & Referrals', icon: Star, bg: 'bg-yellow-100', color: 'text-yellow-600', path: '/portal/rewards', testId: 'portal-rewards-card' },
            { label: 'Profile', sub: 'Preferences', icon: User, bg: 'bg-purple-100', color: 'text-purple-600', path: '/portal/profile', testId: 'portal-profile-card' },
            { label: 'New Booking', sub: 'Book a ride', icon: Car, bg: 'bg-white/20', color: 'text-white', path: '/booking', testId: 'portal-new-booking-card', accent: true },
          ].map((item, idx) => (
            <Card
              key={idx}
              className={`p-4 border-gray-200 hover:shadow-lg transition-all cursor-pointer ${item.accent ? 'bg-red-600 text-white' : ''}`}
              onClick={() => navigate(item.path)}
              data-testid={item.testId}
            >
              <div className="flex items-center gap-3">
                <div className={`h-10 w-10 ${item.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <item.icon className={`h-5 w-5 ${item.color}`} />
                </div>
                <div className="min-w-0">
                  <h4 className="font-semibold text-sm truncate">{item.label}</h4>
                  <p className={`text-xs ${item.accent ? 'text-white/70' : 'text-gray-500'}`}>{item.sub}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Upcoming Bookings */}
        {dashboard?.upcoming_bookings?.length > 0 && (
          <Card className="p-5 sm:p-6 border-gray-200 mb-6" data-testid="portal-upcoming">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-black">Upcoming Bookings</h3>
              <Button variant="ghost" size="sm" onClick={() => navigate('/portal/bookings')} className="text-xs">
                View All <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
            <div className="space-y-3">
              {dashboard.upcoming_bookings.map((booking, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  data-testid={`upcoming-${idx}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Car className="h-5 w-5 text-red-600" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-semibold text-black text-sm truncate">{booking.serviceType}</h4>
                      <p className="text-xs text-gray-500 flex items-center gap-1 truncate"><MapPin className="h-3 w-3 flex-shrink-0" />{booking.pickupLocation}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    <div className="text-right">
                      <p className="font-semibold text-black text-sm">{new Date(booking.pickupDate).toLocaleDateString('en-US', {month:'short', day:'numeric'})}</p>
                      <p className="text-xs text-gray-500">{booking.pickupTime}</p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button onClick={() => navigate(`/track/${booking.bookingId}`)}
                        className="text-[9px] font-semibold bg-red-600 text-white px-2 py-1 rounded active:bg-red-700"
                        data-testid={`track-upcoming-${idx}`}>Track</button>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/calendar/booking/${booking.bookingId}/google`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-[9px] font-semibold bg-blue-50 text-blue-600 px-2 py-1 rounded text-center active:bg-blue-100"
                        data-testid={`gcal-upcoming-${idx}`}>GCal</a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Client Analytics Dashboard */}
        {dashboard?.stats && (
          <div className="grid md:grid-cols-2 gap-4 sm:gap-6 mb-6" data-testid="client-analytics">
            {/* Spending Summary */}
            <Card className="p-5 sm:p-6 border-gray-200">
              <h3 className="text-sm font-bold text-black mb-4 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-green-600" />Your Spending
              </h3>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold text-green-700">${(dashboard.stats.total_spent || 0).toLocaleString()}</p>
                  <p className="text-[10px] text-green-500">Total Spent</p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold text-blue-700">
                    ${dashboard.stats.total_rides > 0 ? Math.round((dashboard.stats.total_spent || 0) / dashboard.stats.total_rides) : 0}
                  </p>
                  <p className="text-[10px] text-blue-500">Avg Per Ride</p>
                </div>
              </div>

              {/* Tier Recommendation */}
              {(dashboard.stats.total_spent || 0) >= 200 && memberTier !== 'penthouse' && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs font-bold text-amber-700 mb-1">Membership Insight</p>
                  <p className="text-[11px] text-amber-600 leading-relaxed">
                    {(dashboard.stats.total_spent || 0) >= 1000
                      ? 'Based on your spending, a Full-Access Executive ($697/mo) tier could save you 25% on every ride plus 8 hrs/mo included.'
                      : (dashboard.stats.total_spent || 0) >= 500
                      ? 'You\'d save with Lite Premier ($195/mo) — 15% off all bookings plus monthly advisory.'
                      : 'Consider Lite Plus ($95/mo) — 10% off all bookings plus quarterly advisory sessions.'}
                  </p>
                  <Button onClick={() => navigate('/membership')} size="sm" className="mt-2 h-8 text-xs bg-amber-600 hover:bg-amber-700 text-white" data-testid="analytics-upgrade-btn">
                    View Tiers
                  </Button>
                </div>
              )}
            </Card>

            {/* Loyalty Progress */}
            <Card className="p-5 sm:p-6 border-gray-200">
              <h3 className="text-sm font-bold text-black mb-4 flex items-center gap-2">
                <Award className="h-4 w-4 text-yellow-600" />Loyalty Progress
              </h3>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="text-center p-3 bg-yellow-50 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold text-yellow-700">{dashboard.stats.loyalty_points || 0}</p>
                  <p className="text-[10px] text-yellow-500">Points Earned</p>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold text-red-700">{dashboard.stats.total_rides || 0}</p>
                  <p className="text-[10px] text-red-500">Total Rides</p>
                </div>
              </div>

              {/* Progress to next tier */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Next milestone</span>
                  <span className="font-bold text-black">
                    {(dashboard.stats.total_rides || 0) < 5 ? '5 rides' : (dashboard.stats.total_rides || 0) < 15 ? '15 rides' : (dashboard.stats.total_rides || 0) < 30 ? '30 rides' : '50 rides'}
                  </span>
                </div>
                <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-yellow-400 to-red-500 rounded-full transition-all"
                    style={{ width: `${Math.min(((dashboard.stats.total_rides || 0) / ((dashboard.stats.total_rides || 0) < 5 ? 5 : (dashboard.stats.total_rides || 0) < 15 ? 15 : (dashboard.stats.total_rides || 0) < 30 ? 30 : 50)) * 100, 100)}%` }} />
                </div>
                <p className="text-[10px] text-gray-400">
                  {(dashboard.stats.total_rides || 0) < 5
                    ? `${5 - (dashboard.stats.total_rides || 0)} more ride${5 - (dashboard.stats.total_rides || 0) === 1 ? '' : 's'} to unlock Condo tier`
                    : (dashboard.stats.total_rides || 0) < 15
                    ? `${15 - (dashboard.stats.total_rides || 0)} more rides to unlock Penthouse tier`
                    : 'Welcome to the top tier! Keep earning points.'}
                </p>
              </div>
            </Card>
          </div>
        )}

        {/* Recent Activity */}
        {dashboard?.recent_bookings?.length > 0 && (
          <Card className="p-5 sm:p-6 border-gray-200" data-testid="portal-recent">
            <h3 className="text-base font-bold text-black mb-4">Recent Activity</h3>
            <div className="space-y-3">
              {dashboard.recent_bookings.slice(0, 5).map((booking, idx) => (
                <div key={idx} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`h-2 w-2 rounded-full flex-shrink-0 ${
                      booking.status === 'completed' ? 'bg-green-500' : booking.status === 'confirmed' ? 'bg-blue-500' : 'bg-yellow-500'
                    }`} />
                    <div className="min-w-0">
                      <p className="font-medium text-black text-sm truncate">{booking.serviceType}</p>
                      <p className="text-xs text-gray-500">{new Date(booking.createdAt || booking.pickupDate).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <p className="font-semibold text-black text-sm">${booking.totalPrice}</p>
                    <p className={`text-xs capitalize ${booking.status === 'completed' ? 'text-green-600' : booking.status === 'confirmed' ? 'text-blue-600' : 'text-yellow-600'}`}>
                      {booking.status?.replace('_', ' ')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Empty State */}
        {(!dashboard?.recent_bookings || dashboard.recent_bookings.length === 0) && (
          <Card className="p-10 sm:p-12 border-gray-200 text-center" data-testid="portal-empty">
            <Car className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-black mb-2">No bookings yet</h3>
            <p className="text-gray-600 mb-6">Start your ACE journey with your first booking!</p>
            <Button onClick={() => navigate('/booking')} className="bg-red-600 hover:bg-red-700 text-white">Book Your First Ride</Button>
          </Card>
        )}
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 sm:hidden" data-testid="portal-mobile-nav">
        <div className="flex items-center justify-around">
          {[
            { label: 'Home', icon: Car, path: '/portal' },
            { label: 'Bookings', icon: Calendar, path: '/portal/bookings' },
            { label: 'Book', icon: Calendar, path: '/booking', accent: true },
            { label: 'Rewards', icon: Star, path: '/portal/rewards' },
            { label: 'Profile', icon: User, path: '/portal/profile' },
          ].map(({ label, icon: Icon, path, accent }) => (
            <button key={path} onClick={() => navigate(path)}
              className={`flex flex-col items-center py-2.5 px-2 min-w-[56px] transition-colors
                ${accent ? 'text-red-600' : window.location.pathname === path ? 'text-red-600' : 'text-gray-400'}`}
              data-testid={`portal-nav-${label.toLowerCase()}`}>
              <Icon className="h-5 w-5" />
              <span className="text-[9px] font-semibold mt-0.5">{label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ClientPortal;
